import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Pagination from 'react-js-pagination';
import { Dropdown, Form } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { IconSearch } from '@tabler/icons-react';

import PaginationLoader from 'components/loaders/PaginationLoader';
import { usersActions } from 'pages/users/containers/usersContainer/actions';
import RegistrationDataRow from 'pages/users/containers/usersContainer/components/registrationData/registrationDataRow';
import {
  REGISTRATION_DATA_COLUMNS,
  REGISTRATION_DATA_DEFAULT_ORDER,
  REGISTRATION_DATA_PAGE_SIZE
} from 'pages/users/containers/usersContainer/components/registrationData/columns';
import { getRegistrationData } from 'selectors';

const tableWidth = REGISTRATION_DATA_COLUMNS.reduce((sum, { width }) => sum + width, 0);

const RegistrationDataTable = (props) => {
  const { enabled, results, count, current_page, is_loading, dispatch } = props;

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(REGISTRATION_DATA_DEFAULT_ORDER);
  const [exporting, setExporting] = useState(false);
  const timer = useRef(null);

  const fetchData = ({ page_number = 1, searchText = search, sortBy = sort }) => {
    dispatch(
      usersActions.fetchRegistrationData({
        page_number,
        searchText,
        order_by: `${sortBy.column},${sortBy.direction}`,
        page_size: REGISTRATION_DATA_PAGE_SIZE
      })
    );
  };

  useEffect(() => {
    fetchData({});
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (enabled !== true) {
    return null;
  }

  const handleSearchChange = (event) => {
    const searchText = event.target.value;
    setSearch(searchText);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchData({ searchText }), 400);
  };

  const onSort = (column) => {
    const direction = sort.column === column && sort.direction === 'asc' ? 'desc' : 'asc';
    const sortBy = { column, direction };
    setSort(sortBy);
    fetchData({ sortBy });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await dispatch(
        usersActions.exportRegistrationData({ searchText: search, order_by: `${sort.column},${sort.direction}` })
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="registration-data">
      <div className="registration-data-header">
        <h3 className="registration-data-title">User Registration Data</h3>
        <div className="registration-data-controls">
          <div className="sites-search-holder registration-search ms-0">
            <IconSearch stroke={1.5} className="ico-search" />
            <Form.Group>
              <Form.Control
                type="text"
                placeholder="Search name, email, TRN, parish, role..."
                name="registration-data-search"
                value={search}
                onChange={handleSearchChange}
                aria-label="Search Registration Data"
              />
            </Form.Group>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary" className="registration-export" disabled={exporting}>
              Export Report
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleExport} disabled={exporting}>
                CSV
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div className="management-table-holder table-holder">
        <Table size="sm" className="registration-data-table" style={{ width: tableWidth }}>
          <colgroup>
            {REGISTRATION_DATA_COLUMNS.map(({ key, width }) => (
              <col key={key} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {REGISTRATION_DATA_COLUMNS.map(({ key, label, sortKey }) => (
                <th key={key} title={label}>
                  {sortKey ? (
                    <span
                      className={sort.column === sortKey ? `sorter ${sort.direction}` : 'sorter unsorted'}
                      onClick={() => onSort(sortKey)}
                    >
                      {label}
                    </span>
                  ) : (
                    <span>{label}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {is_loading ? (
              <tr>
                <td colSpan={REGISTRATION_DATA_COLUMNS.length} className="management-table-frame">
                  <PaginationLoader />
                </td>
              </tr>
            ) : (
              results.map((row) => <RegistrationDataRow key={row.id} columns={REGISTRATION_DATA_COLUMNS} row={row} />)
            )}
          </tbody>
        </Table>
      </div>
      <div className="table-footer mb-4 justify-content-between">
        <span>{`Showing ${results.length} of ${count} users`}</span>
        <Pagination
          activePage={current_page}
          itemsCountPerPage={REGISTRATION_DATA_PAGE_SIZE}
          totalItemsCount={count}
          pageRangeDisplayed={5}
          onChange={(page_number) => fetchData({ page_number })}
        />
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  const { enabled, results, count, current_page, is_loading } = getRegistrationData(state);
  return { enabled, results, count, current_page, is_loading };
}

export default connect(mapStateToProps)(RegistrationDataTable);
